/** Intelligence API bindings (M9). */
import { http } from "@/shared/api/http";

export type ReportCadence = "daily_pulse" | "weekly_digest" | "monthly_review" | "campaign_post_mortem";
export type OpportunityStatus = "open" | "accepted" | "rejected" | "implemented" | "expired";
export type OpportunitySeverity = "info" | "warning" | "high" | "critical";
export type DecisionCategory = "scheduling" | "spend" | "brand" | "campaign" | "publishing" | "strategy";
export type DecisionPriority = "low" | "medium" | "high" | "urgent";
export type DecisionStatus = "proposed" | "accepted" | "rejected" | "implemented" | "expired";
export type BudgetStatus = "healthy" | "warning" | "over" | "hard_stopped" | "emergency_override";
export type VoiceVerdict = "on_brand" | "needs_review" | "off_brand";

export interface BudgetThreshold {
  daily_usd: number;
  monthly_usd: number;
  warning_pct: number;
  hard_stop_pct: number;
  emergency_override: boolean;
}

export interface Budget {
  workspace_id: string;
  threshold: BudgetThreshold;
  spend_today_usd: number;
  spend_month_usd: number;
  estimated_month_end_usd: number;
  status: BudgetStatus;
  last_computed_at: string | null;
}

export interface SpendSlice {
  provider: string | null;
  model: string | null;
  content_type: string | null;
  cost_usd: number;
  calls: number;
}

export interface SpendRollup {
  day_24h: { cost: number; prompt_tokens: number; completion_tokens: number; calls: number };
  day_7d: { cost: number; prompt_tokens: number; completion_tokens: number; calls: number };
  day_30d: { cost: number; prompt_tokens: number; completion_tokens: number; calls: number };
  by_model: SpendSlice[];
  waste_ratio_pct: number;
  stalled_heads: number;
  total_heads: number;
}

export interface Opportunity {
  id: string;
  workspace_id: string;
  kind: string;
  severity: OpportunitySeverity;
  status: OpportunityStatus;
  title: string;
  narrative: string;
  action_hint: string;
  evidence: Record<string, unknown>;
  channel: string | null;
  campaign_pack_id: string | null;
  permanent_asset_id: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface DecisionRecommendation {
  id: string;
  workspace_id: string;
  category: DecisionCategory;
  priority: DecisionPriority;
  status: DecisionStatus;
  recommendation: string;
  reason: string;
  potential_impact: string;
  confidence: number;
  required_inputs: string[];
  expires_at: string | null;
  source_opportunity_id: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  implemented_at: string | null;
}

export interface Briefing {
  workspace_id: string;
  generated_at: string;
  greeting: string;
  platform_score: number;
  yesterday: {
    campaigns_active: number;
    assets_published_today: number;
    ai_cost_24h_usd: number;
    estimated_reach: number | null;
  };
  today_recommendations: string[];
  opportunity_headline: string | null;
  open_opportunities: Opportunity[];
  recommendations: DecisionRecommendation[];
  briefing_text: string;
}

export interface VoiceSummary {
  summary: { score: number | null; on_brand: number; off_brand: number; sample_size: number };
  lowest_scoring: Array<{
    _id: string; asset_id: string; permanent_asset_id: string | null;
    score: number; verdict: VoiceVerdict; hints: string[];
    signals: Record<string, unknown>;
  }>;
}

export interface CampaignScorecard {
  campaign_pack_id: string;
  campaign_name: string;
  planned_assets: number;
  per_stage: Record<string, number>;
  delivered: number;
  fail_rate_pct: number;
  dead_letter: number;
  bottleneck_stage: string | null;
  bottleneck_gap: number;
  completion_pct?: number;
}

export interface AgentSpec {
  id: string;
  workspace_id: string;
  kind: string;
  display_name: string;
  mission: string;
  inputs: string[];
  outputs: string[];
  autonomous_enabled: boolean;
  enabled: boolean;
  last_activity_at: string | null;
  utilisation_pct: number;
}

export interface AgentDryRunPlan {
  workspace_id: string;
  agent_kind: string;
  proposed_action: string;
  rationale: string;
  inputs_available: boolean;
  inputs_missing: string[];
  confidence: number;
}

export interface WorkforceSnapshot {
  agents: AgentSpec[];
  plans: AgentDryRunPlan[];
}

export interface ExecReport {
  id: string;
  workspace_id: string;
  cadence: ReportCadence;
  title: string;
  period_start: string;
  period_end: string;
  takeaways: string[];
  html_body: string | null;
  pdf_asset_ref: { url?: string; storage_key?: string; size_bytes?: number } | null;
  created_at: string;
}

const base = (ws: string) => `/workspaces/${ws}/intelligence`;

export const getSnapshot = (ws: string) => http(`${base(ws)}/snapshot`);
export const getBriefing = (ws: string): Promise<Briefing> => http(`${base(ws)}/advisor/briefing`);
export const getSpendRollup = (ws: string): Promise<SpendRollup> => http(`${base(ws)}/spend/rollup`);
export const getBudget = (ws: string): Promise<Budget> => http(`${base(ws)}/budget`);
export const putBudget = (ws: string, t: BudgetThreshold): Promise<Budget> =>
  http(`${base(ws)}/budget`, { method: "PUT", body: t });
export const getBrand = (ws: string): Promise<VoiceSummary> => http(`${base(ws)}/brand`);
export const rescoreBrand = (ws: string): Promise<{ rescored: number }> =>
  http(`${base(ws)}/brand/rescore`, { method: "POST" });
export const getStalledCampaigns = (ws: string): Promise<CampaignScorecard[]> =>
  http(`${base(ws)}/campaigns/stalled`);
export const getCampaignScorecard = (ws: string, cid: string): Promise<CampaignScorecard> =>
  http(`${base(ws)}/campaigns/${cid}/scorecard`);
export const listOpportunities = (ws: string): Promise<Opportunity[]> =>
  http(`${base(ws)}/opportunities`);
export const acceptOpportunity = (ws: string, id: string) =>
  http(`${base(ws)}/opportunities/${id}/accept`, { method: "POST" });
export const rejectOpportunity = (ws: string, id: string) =>
  http(`${base(ws)}/opportunities/${id}/reject`, { method: "POST" });
export const listRecommendations = (ws: string): Promise<DecisionRecommendation[]> =>
  http(`${base(ws)}/recommendations`);
export const recommendationAction = (
  ws: string, id: string, action: "accept" | "reject" | "implement",
): Promise<DecisionRecommendation> =>
  http(`${base(ws)}/recommendations/${id}/${action}`, { method: "POST" });
export const getWorkforce = (ws: string): Promise<WorkforceSnapshot> =>
  http(`${base(ws)}/workforce`);
export const listReports = (ws: string): Promise<ExecReport[]> =>
  http(`${base(ws)}/reports`);
export const generateReport = (
  ws: string, cadence: ReportCadence, campaign_pack_id?: string,
): Promise<ExecReport> =>
  http(`${base(ws)}/reports/generate`, {
    method: "POST",
    body: { cadence, campaign_pack_id: campaign_pack_id ?? null },
  });
export type PerformanceReviewState = "observed" | "approved" | "rejected";

export interface PerformanceObservation {
  observation_id: string;
  source: string;
  metric: string;
  value: number;
  review_state: PerformanceReviewState;
  observed_at: string;
  channel: string | null;
  target_platform: string | null;
  permanent_asset_id: string | null;
  external_publication_id: string | null;
  campaign_pack_id?: string | null;
}

export interface WorkflowTraceStage { name: string; state: "complete" | "waiting" | "blocked" | "not_started"; count: number; detail: string; blocker: string | null }
export interface WorkflowTrace { campaign_pack_id: string; campaign_name: string; blueprint_id: string | null; complete: boolean; next_action: string; stages: WorkflowTraceStage[] }

export type CustomerOneAuditStatus = "passed" | "action_required" | "deferred_by_founder" | "manual_verification";
export interface CustomerOneAuditCheck { key: string; label: string; status: CustomerOneAuditStatus; detail: string; action_path: string | null }
export interface CustomerOneWalkthroughStep { step: number; key: string; label: string; status: CustomerOneAuditStatus; action_path: string; expected_evidence: string }
export interface CustomerOneAudit {
  workspace_id: string; generated_at: string; core_ready: boolean; blocking_count: number;
  deferred_count: number; manual_count: number; checks: CustomerOneAuditCheck[];
  walkthrough: CustomerOneWalkthroughStep[]; next_action: string; policy: string; walkthrough_policy: string;
}
export const getCustomerOneAudit = (ws: string): Promise<CustomerOneAudit> =>
  http(`${base(ws)}/customer-one-audit`);

export interface PerformanceSnapshot {
  workspace_id: string;
  observations_by_state: Record<string, number>;
  approved_metric_totals: Record<string, number>;
  observed_metric_totals: Record<string, number>;
  approved_by_platform: Record<string, Record<string, number>>;
  current_sources: string[];
  last_observed_at: string | null;
  last_approved_at: string | null;
  truth_basis: "founder_approved_only";
  brain_learning_enabled: boolean;
}

export const getPerformanceSnapshot = (ws: string): Promise<PerformanceSnapshot> =>
  http(`${base(ws)}/performance/snapshot`);
export const listPerformanceObservations = (
  ws: string, state: PerformanceReviewState = "observed",
): Promise<PerformanceObservation[]> =>
  http(`${base(ws)}/performance/observations?state=${state}`);
export const syncZernioPerformance = (ws: string) =>
  http(`${base(ws)}/performance/sync-zernio`, { method: "POST" });
export const approvePerformanceObservation = (
  ws: string, id: string, learning?: { outcome: "winner" | "loser" | "neutral"; evidence_label: string; sample_size: number; confidence: number },
) => http(`${base(ws)}/performance/observations/${id}/approve`, { method: "POST", body: learning });
export const rejectPerformanceObservation = (ws: string, id: string) =>
  http(`${base(ws)}/performance/observations/${id}/reject`, { method: "POST" });
export const getPerformanceWorkflowTrace = (ws: string, packId: string): Promise<WorkflowTrace> =>
  http(`${base(ws)}/performance/workflow-trace/${packId}`);

export interface CompetitiveProvidersEnvelope {
  note?: string;
  interfaces?: string[];
}

export const listCompetitiveProviders = (ws: string): Promise<CompetitiveProvidersEnvelope> =>
  http(`${base(ws)}/competitive/providers`);

export const SEVERITY_TONES: Record<OpportunitySeverity, string> = {
  info: "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200",
  high: "bg-orange-100 text-orange-900 dark:bg-orange-950/60 dark:text-orange-200",
  critical: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
};

export const PRIORITY_TONES: Record<DecisionPriority, string> = {
  low: "bg-zinc-100 text-zinc-900 dark:bg-zinc-950/60 dark:text-zinc-300",
  medium: "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200",
  high: "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200",
  urgent: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
};

export const BUDGET_STATUS_TONES: Record<BudgetStatus, string> = {
  healthy: "text-emerald-500",
  warning: "text-amber-500",
  over: "text-orange-500",
  hard_stopped: "text-rose-500",
  emergency_override: "text-fuchsia-500",
};
